// Login page — visual match for outputs/sanshi-handoff/prototype/index.html
// §login overlay (lines 127-268). Two-column layout: dark brand panel on
// the left with the 叁石 seal + slogan, light form panel on the right.
//
// Phase 1 contract: real auth (Microsoft Entra OIDC) is not yet wired,
// so the form is cosmetic — submitting looks the email up in
// /api/v1/users, maps the matched user's system role to one of the
// existing DevActorPreset values (admin / member / lp), persists the
// chosen identity to localStorage via the auth-store, and navigates to
// /dashboard. Once Entra OIDC lands, the same form swaps its onSubmit
// for a real password POST + JWT exchange — the look stays the same.

import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo.tsx';
import { actorTitleRank, useActor } from '../lib/actor-store.ts';
import type { DevActorPreset } from '../lib/api.ts';
import { api } from '../lib/api.ts';
import { useAuth, useIsSessionValid } from '../lib/auth-store.ts';
// 2026-06-15 user spec: internal portal 跟 Microsoft Entra 脫鉤 — 統一走
// email + password + 自己的 forgot/reset password 流程。 msal 引入移除。

export function LoginPage() {
  const navigate = useNavigate();
  const setSession = useAuth((s) => s.setSession);
  // 2026-09-14 使用者手機「突然沒有辦法進入系統」, Safari 技術細節:
  //   SecurityError: Attempt to use history.replaceState() more than 100 times per 10 seconds
  // 原本這裡只看 email !== null, 但 RequireAuth 用的是 isSessionValid (還會看 expiresAt)。
  // 勾「記住我 (30 天)」滿 30 天那天: email 還在、expiresAt 已過 →
  //   RequireAuth 判定無效 → <Navigate to="/login" replace>
  //   LoginPage 判定已登入 → navigate('/dashboard', { replace })
  // 兩邊互踢成無限迴圈。 Chrome 只會默默節流 (所以電腦上看起來正常),
  // Safari 丟 SecurityError → ErrorBoundary「頁面載入時發生問題」。
  // 兩邊必須用同一個判斷, 不然任何一邊再加條件就會重演。
  const isLoggedIn = useIsSessionValid();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // B2 admin MFA: after the password step returns { mfa_required }, switch to a
  // 6-digit code-entry step (email + password stay in state for the verify call).
  const [mfaStep, setMfaStep] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  // 2026-05-22: api.ts redirects here with ?reason=session_expired when the
  // backend rejects a stale Bearer token. Seed the initial error from that
  // so the user understands why they were bounced (instead of "I clicked
  // logout?"). useState initialiser reads URL once on mount; subsequent
  // setError calls override it.
  const [error, setError] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    if (params.get('reason') === 'session_expired') {
      return '你的登入已過期，請重新登入';
    }
    return null;
  });

  // Already logged in — bounce to dashboard rather than showing form.
  useEffect(() => {
    if (isLoggedIn) navigate('/dashboard', { replace: true });
  }, [isLoggedIn, navigate]);

  // 2026-06-15: Microsoft Entra OIDC removed for internal portal. Used to
  // post-redirect hydrate from MSAL active account; now login = email +
  // password (POST /auth/login) or forgot/reset via /forgot-password.

  // 2026-05-20: email magic-link login removed for internal portal per
  // user request. Email/password (above) is the local-credential path;
  // Microsoft Entra remains the primary SSO. Magic-link sign-up still
  // works at /signup → email confirmation; admin approves → user logs
  // in with their newly-set password (admin assigns during approval).

  const setLocalToken = useAuth((s) => s.setLocalToken);

  interface LoginActor {
    id: number;
    email: string;
    name: string;
    name_zh?: string | null;
    role: 'admin' | 'member';
    is_sysadmin?: boolean;
    title_rank?: number;
  }
  interface LoginSuccess {
    token: string;
    actor: LoginActor;
  }

  // Shared success handling for both the direct (member) login and the
  // post-OTP (admin) login — persist the JWT + session, go to dashboard.
  const applyLoginSuccess = (r: LoginSuccess) => {
    setLocalToken(r.token);
    // P1: admin 拆兩種 — 系統管理員(is_sysadmin) → 'admin'；否則(Partner 職級
    // 只是業務 admin) → 'partner'。member 不變。
    const preset: DevActorPreset =
      r.actor.role === 'admin' ? (r.actor.is_sysadmin ? 'admin' : 'partner') : 'member';
    setSession({
      email: r.actor.email,
      name: r.actor.name,
      nameZh: r.actor.name_zh ?? null,
      actor: preset,
      rememberDays: remember ? 30 : null,
    });
    // P2: 用後端真實 title_rank 覆寫（preset 推的可能不準，例 VP 也是 'member'）。
    useActor.getState().setTitleRank(r.actor.title_rank ?? actorTitleRank(preset));
    navigate('/dashboard', { replace: true });
  };

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // 2026-05-20: real email + password login via POST /api/v1/auth/login.
      // Backend bcrypt-compares against app_user.password_hash.
      // B2: admins get { mfa_required } instead of a token → show code step.
      const r = await api<LoginSuccess & { mfa_required?: boolean; email?: string }>(
        '/api/v1/auth/login',
        {
          actor: 'admin', // unused — login is public, no auth header sent for it
          method: 'POST',
          json: { email: email.trim(), password, remember_me: remember },
        },
      ).catch((err) => {
        const msg = err instanceof Error ? err.message : String(err);
        if (msg.includes('mfa_email_failed')) throw new Error('驗證碼寄送失敗，請稍後再試');
        if (msg.includes('401') || msg.includes('invalid_credentials')) {
          throw new Error('帳號或密碼錯誤');
        }
        throw err;
      });
      if (r.mfa_required) {
        setOtpCode('');
        setMfaStep(true);
        return;
      }
      applyLoginSuccess(r);
      return;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return;
    } finally {
      setSubmitting(false);
    }
  };

  // B2 step 2: exchange the emailed 6-digit code for the session JWT.
  const submitOtp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const r = await api<LoginSuccess>('/api/v1/auth/login/verify-otp', {
        actor: 'admin',
        method: 'POST',
        json: { email: email.trim(), code: otpCode.trim(), remember_me: remember },
      }).catch((err) => {
        const msg = err instanceof Error ? err.message : String(err);
        if (msg.includes('too_many_attempts') || msg.includes('429')) {
          throw new Error('驗證碼錯誤次數過多，請返回重新登入以取得新的驗證碼');
        }
        if (msg.includes('invalid_or_expired_code') || msg.includes('401')) {
          throw new Error('驗證碼錯誤或已過期');
        }
        throw err;
      });
      applyLoginSuccess(r);
      return;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return;
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ts-login-bg fixed inset-0 ts-fade-in">
      <div className="relative h-full flex">
        {/* Left brand panel — hidden on mobile. 2026-05-11 重做配色對齊
            tristonecap.com: 純深色 slate + 三連環 logo + 白色 serif + 標語
            「私有市場 · 永續資本 · 長期價值」 + motto「共創基業 / Backing. Building. Becoming.」 */}
        <div className="hidden lg:flex flex-col justify-between w-[48%] p-12 xl:p-16 relative">
          <div className="ts-rise">
            <div className="flex items-center gap-3 mb-2">
              <BrandLogo className="w-14 h-14 text-white" />
              <div>
                <div className="ts-brand-text text-2xl font-medium text-white">
                  叁石資本管理顧問
                </div>
                <div className="ts-brand-wide text-white/55 text-[11px] mt-1">
                  Tristone Capital Management
                </div>
              </div>
            </div>
          </div>

          <div className="ts-rise-delay-1">
            <div className="ts-brand-wide text-white/55 text-xs mb-6">Tristone Capital</div>
            <div className="ts-brand-text text-white text-6xl xl:text-7xl leading-tight mb-8">
              叁石資本
            </div>
            <div className="ts-brand-text text-white/70 text-sm tracking-[0.3em] mb-10">
              私有市場 · 永續資本 · 長期價值
            </div>
            {/* 2026-08-05 使用者改 motto：助人興業 → 共創基業 / Backing. Building. Becoming.
                英文行原本沒有，這次一起補上（中文字距 0.4em，英文用一般字距 —— 英文
                句子拉開字距會散掉，不套同一組 tracking）。 */}
            <div className="flex items-stretch gap-4">
              <div className="w-0.5 bg-white/30" />
              <div>
                <div className="ts-brand-text text-white/85 text-2xl tracking-[0.4em]">
                  共創基業
                </div>
                <div className="ts-brand-text text-white/60 text-sm mt-2">
                  Backing. Building. Becoming.
                </div>
              </div>
            </div>
          </div>

          <div className="ts-rise-delay-3 text-[11px] text-white/35 leading-relaxed ts-brand-wide tracking-[0.15em]">
            <div>© 2026 Tristone Capital Management</div>
            <div className="mt-1 normal-case tracking-normal">
              基金與投資管理系統 · 叁石成員入口
            </div>
          </div>
        </div>

        {/* Right form panel */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md ts-rise-delay-1">
            {/* Mobile logo */}
            <div className="lg:hidden flex items-center gap-3 mb-8">
              <BrandLogo className="w-12 h-12 text-white" />
              <div>
                <div className="ts-brand-text text-2xl font-medium text-white">叁石資本</div>
                <div className="ts-brand-wide text-white/55 text-[10px] mt-0.5">
                  Tristone Capital
                </div>
              </div>
            </div>

            <h2 className="ts-brand-text text-3xl font-semibold text-white mb-2">
              {mfaStep ? '輸入驗證碼' : '歡迎回來'}
            </h2>
            <p className="text-white/55 text-sm mb-8">
              {mfaStep ? '完成兩步驟驗證以登入內部系統' : '使用公司帳號登入'}
            </p>

            {/* 2026-06-15: Microsoft Entra SSO removed per user spec — internal
                portal 走自己的 email + password + forgot-password 流程 */}

            {mfaStep ? (
              <form onSubmit={submitOtp} className="space-y-4">
                <p className="text-white/60 text-sm leading-relaxed">
                  我們已將 6 位數驗證碼寄到{' '}
                  <span className="text-white/90 font-medium">{email}</span>
                  ，請輸入以完成登入。
                </p>
                <div>
                  <label className="block text-xs font-medium text-white/70 mb-1.5" htmlFor="otp">
                    驗證碼
                  </label>
                  <input
                    id="otp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="ts-input w-full px-4 py-2.5 rounded-md text-lg font-mono text-center tracking-[0.6em]"
                  />
                </div>

                {error && (
                  <div className="bg-red-500/15 border border-red-500/30 text-red-200 text-xs px-3 py-2 rounded">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || otpCode.length !== 6}
                  className="ts-btn-primary w-full px-4 py-3 rounded-md font-semibold text-sm tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? '驗證中…' : '完成登入'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMfaStep(false);
                    setOtpCode('');
                    setError(null);
                  }}
                  className="w-full text-center text-xs text-white/50 hover:text-white/75 cursor-pointer"
                >
                  ← 返回重新登入
                </button>
              </form>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <label
                    className="block text-xs font-medium text-white/70 mb-1.5"
                    htmlFor="loginEmail"
                  >
                    電子郵件
                  </label>
                  <input
                    id="loginEmail"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@tristonecap.com"
                    className="ts-input w-full px-4 py-2.5 rounded-md text-sm"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label
                      className="block text-xs font-medium text-white/70"
                      htmlFor="loginPassword"
                    >
                      密碼
                    </label>
                    <Link to="/forgot-password" className="text-xs ts-link cursor-pointer">
                      忘記密碼？
                    </Link>
                  </div>
                  <div className="relative">
                    <input
                      id="loginPassword"
                      type={showPassword ? 'text' : 'password'}
                      required
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="請輸入密碼"
                      className="ts-input w-full px-4 py-2.5 pr-10 rounded-md text-sm font-mono"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
                      aria-label="顯示 / 隱藏密碼"
                    >
                      {showPassword ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      className="w-4 h-4 rounded border-white/30 bg-white/10 text-amber-500 focus:ring-amber-500/40"
                    />
                    記住我（30 天）
                  </label>
                </div>

                {error && (
                  <div className="bg-red-500/15 border border-red-500/30 text-red-200 text-xs px-3 py-2 rounded">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="ts-btn-primary w-full px-4 py-3 rounded-md font-semibold text-sm tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? '登入中…' : '登入系統'}
                </button>
              </form>
            )}

            {/* Signup link — hidden during the OTP step */}
            {!mfaStep && (
              <div className="text-center text-xs text-white/55 mt-5">
                還沒有帳號？
                <Link to="/signup" className="ts-link cursor-pointer">
                  立即申請
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ----- visual sub-components -----
// 2026-06-15: BrandLogo extracted to components/BrandLogo.tsx so the auth-flow
// pages (ForgotPasswordPage / ResetPasswordPage) can reuse.
// MicrosoftLogo removed — Entra SSO 已脫鉤
